import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Billboard, Text } from '@react-three/drei';
import { Color, DoubleSide, Mesh } from 'three';

import { RenderOrder } from '@paragon/three-shared';

import { useAppSelector } from '../../components/hooks';
import { is3DLayoutCameraInUse } from '../../store/selectors/layoutModeSelectors';
import { isLabelTooTallForViewport, subscribeToAntiZoomFactor, useAntiZoomCheck } from '../antiZoom';
import { getActiveCamera } from '../reactThreeFiberBindings';

export interface LabelVisualProps {
  readonly position?: readonly [number, number, number];
  readonly fontSize: number;
  readonly children: string;
  readonly rotation?: number;
  readonly onTextSync?: (troika: Mesh) => void;
  readonly color?: Color | string | number;
  readonly opacity?: number;
  readonly outlineColor?: number;
  readonly outlineOpacity?: number;
  readonly outlineWidthRatio?: number;
  readonly visible?: boolean;
  readonly alwaysOnTop?: boolean; // defaults to true
  readonly renderOrder?: RenderOrder;
  readonly applyAntiZoomFactor?: boolean;
}

export function LabelVisual({
  children,
  position,
  fontSize,
  rotation,
  onTextSync,
  color,
  opacity,
  outlineColor,
  outlineOpacity,
  outlineWidthRatio = 0.05,
  visible = true,
  alwaysOnTop = true,
  renderOrder = RenderOrder.GlyphVisual,
  applyAntiZoomFactor = false,
}: LabelVisualProps) {
  const [antiZoomFactor, setAntiZoomFactor] = useState<number>(() =>
    applyAntiZoomFactor ? getActiveCamera().getAntiZoomFactor() : 1,
  );

  useEffect(() => {
    if (!applyAntiZoomFactor) return;

    return subscribeToAntiZoomFactor(() => {
      setAntiZoomFactor(getActiveCamera().getAntiZoomFactor());
    });
  }, [applyAntiZoomFactor]);

  const finalFontSize = fontSize * antiZoomFactor;

  const is3D = useAppSelector(is3DLayoutCameraInUse);
  const hiddenByHeight =
    useAntiZoomCheck((level) =>
      // 1.2 is approximately the height-to-font-size ratio for Roboto
      isLabelTooTallForViewport(fontSize * (applyAntiZoomFactor ? level : 1) * 1.2, level),
    ) && !is3D;

  const rotation3d = useMemo<[number, number, number]>(() => [0, 0, rotation ?? 0], [rotation]);

  return (
    <Suspense>
      {/* The position needs to be set on Billboard instead of the Text for the bill-boarding to work correctly. */}
      <Billboard position={position} visible={visible && !hiddenByHeight}>
        <Text
          onSync={onTextSync}
          fontSize={finalFontSize}
          rotation={rotation3d}
          renderOrder={renderOrder}
          outlineWidth={finalFontSize * outlineWidthRatio}
          outlineColor={outlineColor}
          outlineOpacity={outlineOpacity}
        >
          {children}
          <meshBasicMaterial
            side={DoubleSide}
            transparent
            opacity={opacity}
            depthTest={!alwaysOnTop}
            depthWrite={!alwaysOnTop}
            toneMapped={false}
            color={color}
          />
        </Text>
      </Billboard>
    </Suspense>
  );
}

export interface LabelSize {
  readonly width: number;
  readonly height: number;
}

/**
 * Useful when repositioning a label based on its size.
 */
export function getLabelSizeFromTextMesh(text: Mesh): LabelSize {
  if (!text.geometry.boundingBox) throw new Error('Expected bounding box to be defined');
  const { min, max } = text.geometry.boundingBox.clone().applyMatrix4(text.matrixWorld);
  return { width: max.x - min.x, height: max.y - min.y };
}
